import { useState, useEffect } from 'react';
import { AuthorityCard } from '@/components/territories/AuthorityCard';
import { AuthorityAssignmentDialog } from '@/components/territories/AuthorityAssignmentDialog';
import { usePermissions } from '@/hooks/usePermissions';
import {
  getNationalAuthorityPositions,
  updateNationalAuthority,
  getDistrictsWithStats,
} from '@/data/territoryRepository';
import type { AuthorityPosition } from '@/models/authorityPosition';
import { NATIONAL_ROLES, ROLE_LABELS } from '@/models/authorityPosition';
import type { District } from '@/models/district';
import type { Minister } from '@/models/minister';
import { Loader2, MapPin } from 'lucide-react';
import { useInstitution } from '@/features/institutions/hooks';
import { InstitutionAuthoritiesTab } from '@/features/institutions/components/InstitutionAuthoritiesTab';

type AuthoritySlot = {
  role: string;
  label: string;
  minister: AuthorityPosition['minister'] | null;
};

export default function NacionalInformationTab() {
  const { can } = usePermissions();

  const [positions, setPositions] = useState<AuthorityPosition[]>([]);
  const [districts, setDistricts] = useState<(District & { activeMemberCount: number })[]>([]);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [loadingDistricts, setLoadingDistricts] = useState(true);

  const [assignmentDialog, setAssignmentDialog] = useState<{
    isOpen: boolean;
    slot: AuthoritySlot | null;
  }>({ isOpen: false, slot: null });

  // National authorities: positions without district or region (national scope)
  const canAssign = can('authority_position.create', {}) || can('authority_position.edit', {});
  const { data: directorioInstitution } = useInstitution("directorio-nacional");

  const loadPositions = async () => {
    setLoadingPositions(true);
    try {
      const data = await getNationalAuthorityPositions();
      setPositions(data);
    } finally {
      setLoadingPositions(false);
    }
  };

  useEffect(() => {
    loadPositions();
    getDistrictsWithStats()
      .then(setDistricts)
      .catch(console.error)
      .finally(() => setLoadingDistricts(false));
  }, []);

  // Build authority slots from active positions
  const activeByRole = new Map<string, AuthorityPosition>(
    positions.filter((p) => !p.ended_at).map((p) => [p.role, p])
  );

  const slots: AuthoritySlot[] = NATIONAL_ROLES.map((role) => ({
    role,
    label: ROLE_LABELS[role],
    minister: activeByRole.get(role)?.minister ?? null,
  }));

  // IDs already assigned to national roles (to prevent double-assigning)
  const assignedMinisterIds = slots
    .filter((s) => s.minister)
    .map((s) => s.minister!.id);

  const handleSaveAssignment = async (ministerId: string | null, startedAt: string) => {
    if (!assignmentDialog.slot) return;
    await updateNationalAuthority(
      assignmentDialog.slot.role as Parameters<typeof updateNationalAuthority>[0],
      ministerId,
      startedAt
    );
    await loadPositions();
  };

  return (
    <div className="p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Authorities Section */}
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="mb-6 text-lg font-semibold">Autoridades Nacionales</h2>
          {loadingPositions ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {slots.map((slot) => (
                <AuthorityCard
                  key={slot.role}
                  positionLabel={slot.label}
                  minister={slot.minister as Minister | null | undefined}
                  canAssign={canAssign}
                  onAssign={() => setAssignmentDialog({ isOpen: true, slot })}
                />
              ))}
            </div>
          )}
        </div>

        {/* Directorio Nacional — institution-specific office positions */}
        {directorioInstitution && (
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="mb-6 text-lg font-semibold">Autoridades de Oficina</h2>
            <InstitutionAuthoritiesTab
              institutionId={directorioInstitution.id}
              nationalOnly
              hideHistory
            />
          </div>
        )}

        {/* All Districts */}
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="mb-6 text-lg font-semibold">Distritos</h2>
          {loadingDistricts ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : districts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay distritos registrados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left text-sm font-semibold">Código</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Nombre</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Ministros Activos</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Superintendente</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Vice Superintendente</th>
                  </tr>
                </thead>
                <tbody>
                  {districts.map((d) => (
                    <tr key={d.id} className="border-b border-border hover:bg-muted/50">
                      <td className="px-4 py-3 text-sm font-medium">{d.code}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <MapPin className="size-3.5 shrink-0 text-muted-foreground" />
                          {d.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm">{d.activeMemberCount}</td>
                      <td className="px-4 py-3 text-sm">
                        {d.superintendent ? (
                          <div>
                            <p className="font-medium">{d.superintendent.adb_code}</p>
                            <p className="text-xs text-muted-foreground">{d.superintendent.full_name}</p>
                          </div>
                        ) : (
                          <span className="italic text-muted-foreground">Sin asignar</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {d.vice_superintendent ? (
                          <div>
                            <p className="font-medium">{d.vice_superintendent.adb_code}</p>
                            <p className="text-xs text-muted-foreground">{d.vice_superintendent.full_name}</p>
                          </div>
                        ) : (
                          <span className="italic text-muted-foreground">Sin asignar</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {assignmentDialog.slot && (
        <AuthorityAssignmentDialog
          isOpen={assignmentDialog.isOpen}
          onClose={() => setAssignmentDialog({ isOpen: false, slot: null })}
          positionLabel={assignmentDialog.slot.label}
          currentMinister={assignmentDialog.slot.minister as Minister | null | undefined}
          onSave={handleSaveAssignment}
          districtId={null}
          regionId={null}
          assignedMinisterIds={assignedMinisterIds}
        />
      )}
    </div>
  );
}
